import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Plus, CalendarClock, Activity, FlaskConical, AlertCircle, Video } from "lucide-react";

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-2 flex items-end gap-2">
        <span className="text-3xl font-sans font-bold tracking-tight">{value}</span>
        {accent && <span className={`h-2 w-2 rounded-full mb-2 ${accent}`} />}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats")
      .then((r) => setStats(r.data))
      .catch((e) => {
        // If the session is invalid, boot the user to landing with a message
        // instead of showing a cryptic "Request failed with status code 401".
        if (e?.response?.status === 401) {
          setUser(null);
          try { window.sessionStorage.setItem("auth_error", "Your session expired — please sign in again."); } catch {}
          window.location.replace("/");
          return;
        }
        const msg = e?.response?.data?.detail || e?.message || "Could not load dashboard";
        setError(msg);
      });
  }, [setUser]);

  if (error) return <div className="p-8 text-red-600" data-testid="dashboard-error">Error: {error}</div>;

  const name = user?.name?.split(" ")[0] || "Researcher";
  const trial = stats?.trial;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Overview</div>
          <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-1">
            Hello, {name}.
          </h1>
          <p className="text-sm font-body text-zinc-500 mt-1">
            Here is what your lab looks like today.
          </p>
        </div>
        <Link to="/experiments/new">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="dashboard-new-experiment-btn">
            <Plus className="h-4 w-4 mr-2" /> New experiment
          </Button>
        </Link>
      </div>

      {/* Trial banner */}
      {trial && !trial.subscription_active && (
        <div
          className={`flex items-center justify-between gap-4 rounded-md border p-4 ${
            trial.trial_expired
              ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20"
              : "border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/20"
          }`}
          data-testid="trial-banner"
        >
          <div className="flex items-center gap-3 text-sm font-body">
            <AlertCircle className={`h-4 w-4 ${trial.trial_expired ? "text-red-600" : "text-blue-600"}`} />
            {trial.trial_expired ? (
              <span>Your free trial has ended. Upgrade to keep full access to exports and uploads.</span>
            ) : (
              <span><strong>{trial.days_left} days left</strong> in your free trial. All premium features are unlocked.</span>
            )}
          </div>
          <Link to="/settings">
            <Button variant="outline" size="sm" data-testid="upgrade-btn">Upgrade</Button>
          </Link>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Total" value={stats?.totals?.total ?? 0} accent="bg-zinc-400" />
        <StatCard label="Planned" value={stats?.totals?.planned ?? 0} accent="bg-zinc-400" />
        <StatCard label="In Progress" value={stats?.totals?.in_progress ?? 0} accent="bg-blue-500" />
        <StatCard label="Completed" value={stats?.totals?.completed ?? 0} accent="bg-emerald-500" />
        <StatCard label="Success rate" value={`${stats?.totals?.success_rate ?? 0}%`} accent="bg-amber-500" />
      </div>

      {/* Two-column: upcoming + recent */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Recent experiments */}
        <div className="lg:col-span-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-blue-600" />
              <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                Recent experiments
              </h2>
            </div>
            <Link to="/experiments" className="text-xs font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50" data-testid="dashboard-view-all-experiments">
              View all →
            </Link>
          </div>
          {stats?.recent_experiments?.length ? (
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {stats.recent_experiments.map((e) => (
                <li key={e.id} className="px-5 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition-colors">
                  <Link to={`/experiments/${e.id}`} className="flex items-center justify-between gap-4" data-testid={`recent-exp-${e.id}`}>
                    <div className="min-w-0">
                      <div className="font-body font-medium truncate">{e.title}</div>
                      <div className="text-xs font-mono text-zinc-500 mt-0.5">
                        updated {new Date(e.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <StatusBadge status={e.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No experiments yet"
              body="Create your first experiment to get started."
              cta={<Link to="/experiments/new"><Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="empty-new-exp">New experiment</Button></Link>}
            />
          )}
        </div>

        {/* Upcoming */}
        <div className="lg:col-span-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-blue-600" />
              <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                Upcoming
              </h2>
            </div>
            <Link to="/calendar" className="text-xs font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50">
              Calendar →
            </Link>
          </div>

          {/* Upcoming meetings */}
          {stats?.upcoming_meetings?.length ? (
            <div data-testid="dashboard-upcoming-meetings">
              <div className="px-5 pt-4 pb-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Lab meetings
              </div>
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {stats.upcoming_meetings.map((m) => (
                  <li
                    key={m.id}
                    className="px-5 py-3 flex items-start justify-between gap-3"
                    data-testid={`dashboard-meeting-${m.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Video className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                        <Link
                          to={`/labs/${m.lab_id}`}
                          className="font-body text-sm font-medium truncate hover:underline"
                        >
                          {m.title}
                        </Link>
                      </div>
                      <div className="text-xs font-mono text-zinc-500 mt-0.5 truncate">
                        {m.lab_name ? `${m.lab_name} · ` : ""}
                        {new Date(m.scheduled_at).toLocaleString()}
                      </div>
                    </div>
                    {m.url && (
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-mono text-blue-600 hover:underline shrink-0"
                        data-testid={`dashboard-meeting-join-${m.id}`}
                      >
                        Join →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Upcoming reminders */}
          {stats?.upcoming_reminders?.length ? (
            <div>
              <div className="px-5 pt-4 pb-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Reminders
              </div>
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {stats.upcoming_reminders.map((r) => (
                  <li key={r.id} className="px-5 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-body text-sm truncate">{r.title}</div>
                      <div className="text-xs font-mono text-zinc-500">{new Date(r.date).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {!stats?.upcoming_meetings?.length && !stats?.upcoming_reminders?.length && (
            <EmptyState title="Nothing scheduled" body="Meetings and reminders will appear here." />
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
        <div className="flex items-center gap-2 p-5 border-b border-zinc-200 dark:border-zinc-800">
          <Activity className="h-4 w-4 text-blue-600" />
          <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
            Recent observations
          </h2>
        </div>
        {stats?.recent_observations?.length ? (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {stats.recent_observations.map((o) => (
              <li key={o.id} className="px-5 py-3">
                <div className="font-body text-sm text-zinc-700 dark:text-zinc-300">{o.content}</div>
                <div className="text-xs font-mono text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No observations yet" body="Log observations inside an experiment." />
        )}
      </div>
    </div>
  );
}

function EmptyState({ title, body, cta }) {
  return (
    <div className="p-10 text-center space-y-2">
      <div className="font-sans font-semibold">{title}</div>
      <div className="text-sm font-body text-zinc-500">{body}</div>
      {cta && <div className="pt-2">{cta}</div>}
    </div>
  );
}
